// quotePdf.ts — shared offerte → PDF generator.
//
// Used by both AdminDashboard (Filip / superadmin / owner) and CustomerDashboard
// (end-customer downloads their own quote). The HTML layout opens in a new
// window where the user can save as PDF or print.
//
// Inputs are normalized: callers translate their own quote shape into PdfQuote
// + PdfCompany before calling. This keeps the PDF logic stable regardless of
// whether it's an AdminQuote, ApiQuote, or ResellerQuote feeding it.
//
// XSS: all customer-controlled strings are escapeHtml'd. Do NOT remove this —
// customers can register with arbitrary text in their name / email / btw.

import { PRODUCTS } from '../store/productData';

// ─── Public types ────────────────────────────────────────────────────────────

export interface PdfCompany {
  name: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  btw: string;
  email: string;
  phone: string;
  logo_url?: string;
}

export interface PdfQuote {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string | null;
  customer_btw_number?: string | null;
  status: string;
  total_excl_vat: number;
  total_incl_vat: number;
  is_b2b?: number | boolean;
  notes_nl?: string | null;
  created_at: number;
  expires_at?: number | null;
  /** Klant's woon/bedrijfsadres (factuuradres). Optioneel — kan in notes_nl
   *  zitten (oude quotes) of als gestructureerd veld (nieuwe quotes). */
  customer_address?: string | null;
  /** Werkadres / installatie-adres. Wanneer gezet en verschillend van het
   *  factuuradres, toont de PDF een prominent "Plaatsing op:"-blok. */
  installation_address?:     string | null;
  installation_postal_code?: string | null;
  installation_city?:        string | null;
  /** Wenst de klant dat wij een installateur regelen? */
  wants_installer?:  number | boolean | null;
  installer_choice?: string | null;
  /** B2B bedrijfsnaam — getoond onder klantnaam in het KLANT-blok. */
  customer_company_name?: string | null;
}

export interface PrintQuotePdfOptions {
  quote: PdfQuote;
  company: PdfCompany;
  /** True when the PDF should show "Powered by Het Gevelsteenbedrijf" header.
   *  Customers always get owner branding directly → false. */
  isResellerRouted: boolean;
  /** Translation function from the calling component's useLanguage(). */
  t: (key: string) => string;
  /** v3.2.117 — 'download' (default) triggert doc.save() naar de
   *  Downloads-folder. 'preview' opent de PDF in een nieuw browser-tabblad
   *  zodat Filip eerst kan lezen wat er in staat. */
  mode?: 'download' | 'preview';
  /** v3.2.124 — vooraf-geopend browser-venster, doorgegeven uit de
   *  click-handler. window.open() MOET in de directe user-gesture context
   *  gebeuren anders blokkeert de pop-up blocker. De caller opent het
   *  venster synchroon op klik en geeft het hier door zodat we de HTML
   *  er ná de async chunks-build in kunnen schrijven. */
  targetWindow?: Window | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function escapeHtml(s: string | null | undefined): string {
  // Defensief: een undefined value (vaak een ontbrekende translation key
  // of een null klant-veld) crashte voorheen met "undefined is not an
  // object (evaluating 's.replace')" → blokkeerde de hele PDF-generatie.
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c)
  );
}

function formatDate(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatEuro(val: number | null | undefined): string {
  if (val == null) return '—';
  return `€${val.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Parse "Key: value" entries from a notes_nl string into a map.
 *  Accepteert zowel newline-separator (QuotePage, oudere quotes) als
 *  pipe-separator " | " (CalculatorPage v3.2). Voorheen werd pipe-format
 *  niet herkend → alles na de eerste key kwam als één gigantische value
 *  binnen, waardoor PRODUCT/CONFIGURATIE/BREAKDOWN leeg bleven in de PDF. */
function parseQuoteNotes(notes: string | null | undefined): Record<string, string> {
  if (!notes) return {};
  const parsed: Record<string, string> = {};
  const lines = notes.split(/\s*\n\s*|\s+\|\s+/);
  for (const line of lines) {
    const m = line.match(/^([^:]+):\s*(.+)$/);
    if (m) parsed[m[1].trim()] = m[2].trim();
  }
  return parsed;
}

function findProductFromNotes(notes: Record<string, string>): typeof PRODUCTS[number] | undefined {
  const productName = notes['Product'];
  if (!productName) return undefined;
  return PRODUCTS.find(p => p.name === productName);
}

// ─── Main export ─────────────────────────────────────────────────────────────

export async function printQuotePDF(opts: PrintQuotePdfOptions): Promise<void> {
  const { quote: q, company, isResellerRouted, t } = opts;

  // [v3.2.122 — diagnose] Log alle PDF-input zodat Filip in DevTools → Console
  // kan zien welke velden binnenkomen. Mocht klantnaam/B2B/notes leeg lijken,
  // dan ligt het of aan de quote-record in DB, of aan de admin-API mapping —
  // en dat zien we hier meteen voor we de chunks gaan bouwen.
  console.log('[PDF] printQuotePDF input voor offerte', q.id.substring(0, 8).toUpperCase(), ':', {
    customer_name: q.customer_name,
    customer_email: q.customer_email,
    customer_phone: q.customer_phone,
    customer_btw_number: q.customer_btw_number,
    customer_address: q.customer_address,
    customer_company_name: q.customer_company_name,
    is_b2b: q.is_b2b,
    status: q.status,
    total_excl_vat: q.total_excl_vat,
    total_incl_vat: q.total_incl_vat,
    wants_installer: q.wants_installer,
    installer_choice: q.installer_choice,
    notes_nl_length: q.notes_nl?.length ?? 0,
    notes_nl_preview: q.notes_nl?.substring(0, 400) ?? '(leeg)',
    company_name: company.name,
    isResellerRouted,
  });
  // v3.2.62 — directe PDF-download i.p.v. browser-venster met print dialog.
  // Filip's klacht: 'die schieten naar een browser window dat kan niet de
  // bedoeling zijn'. Opbouw is hetzelfde HTML, maar we renderen het in een
  // verstopte div en converteren naar PDF via html2pdf — geen pop-up meer,
  // wel een echt .pdf bestand op de computer van de gebruiker.
  const chunks: string[] = [];
  const wd = (s: string) => chunks.push(s);

  const notes = parseQuoteNotes(q.notes_nl);
  const product = findProductFromNotes(notes);
  const vatAmount = q.total_incl_vat - q.total_excl_vat;
  const quoteDate = formatDate(q.created_at);
  const expiryDate = q.expires_at ? formatDate(q.expires_at) : '';

  // v4.0.2 — Localize NL-tokens in notes_nl values voor PL/FR/EN PDFs.
  // Backward-compatible: als notes_nl al in andere taal staat (bv. via
  // groutColorMap), dan matcht de regex niets en blijft de string ongewijzigd.
  // Wraps elke token in word-boundaries om "grijsachtig" niet te raken.
  const localizeValue = (val: string): string => {
    const pieces = t('pdf.unit.pieces');
    return val
      .replace(/\bzakken\b/gi, t('pdf.unit.bags'))
      .replace(/(\d+)\s+st\b/g, `$1 ${pieces}`)
      .replace(/\bgrijs\b/gi, t('calc.grey'))
      .replace(/\bwit\b/gi, t('calc.white'))
      .replace(/\bantraciet\b/gi, t('calc.anthracite'))
      .replace(/\bzand\b/gi, t('calc.sand'));
  };

  // Build detail rows from parsed notes
  // v4 — labels via t() zodat ze in NL/FR/EN/PL renderen.
  // Values worden gelokaliseerd via localizeValue() voor eenheden + voegkleur.
  const detailRows: { label: string; value: string }[] = [];
  if (notes['Product']) detailRows.push({ label: t('pdf.cfg.product'), value: notes['Product'] });
  if (product?.supplierCode) detailRows.push({ label: t('pdf.cfg.articleNr'), value: product.supplierCode });
  if (notes['Kleur'] || product?.color) {
    detailRows.push({ label: t('pdf.cfg.colorTexture'), value: notes['Kleur'] || `${product?.color} | ${product?.texture}` });
  }
  if (product?.dimensions) detailRows.push({ label: t('pdf.cfg.dimensions'), value: product.dimensions });
  if (notes['Oppervlakte']) {
    detailRows.push({ label: t('pdf.cfg.area'), value: notes['Oppervlakte'].includes('m²') ? notes['Oppervlakte'] : notes['Oppervlakte'] + ' m²' });
  }
  if (notes['Voegbreedte']) detailRows.push({ label: t('pdf.cfg.jointWidth'), value: notes['Voegbreedte'] });
  if (notes['Voegkleur']) detailRows.push({ label: t('pdf.cfg.jointColor'), value: localizeValue(notes['Voegkleur']) });
  if (notes['Snijverlies']) detailRows.push({ label: t('pdf.cfg.cuttingLoss'), value: notes['Snijverlies'] });
  if (notes['Stenen']) detailRows.push({ label: t('pdf.cfg.stripCount'), value: localizeValue(notes['Stenen']) });
  if (notes['Hoeken']) detailRows.push({ label: t('pdf.cfg.cornerPieces'), value: localizeValue(notes['Hoeken']) });
  if (notes['Lijm']) detailRows.push({ label: t('pdf.cfg.glue'), value: localizeValue(notes['Lijm']) });
  if (notes['Voegsel']) detailRows.push({ label: t('pdf.cfg.grout'), value: localizeValue(notes['Voegsel']) });

  // Price breakdown rows
  const priceRows: { label: string; value: string }[] = [];
  if (notes['Baksteenstrips']) priceRows.push({ label: t('pdf.cfg.bricks'), value: notes['Baksteenstrips'] });
  if (notes['Hoekstenen']) priceRows.push({ label: t('pdf.cfg.cornerStones'), value: notes['Hoekstenen'] });
  if (notes['Lijm']) {
    const lijmPrice = notes['Lijm'].match(/€[\d.,]+/)?.[0];
    if (lijmPrice) priceRows.push({ label: t('pdf.cfg.glue'), value: lijmPrice });
  }
  if (notes['Voegsel']) {
    const voegPrice = notes['Voegsel'].match(/€[\d.,]+/)?.[0];
    if (voegPrice) priceRows.push({ label: t('pdf.cfg.grout'), value: voegPrice });
  }

  // Product image
  const productImage = product?.images?.[0] || '';
  const imageUrl = productImage ? `${window.location.origin}${productImage}` : '';

  // Simple-quote format (from QuotePage): Type, Bedrijf, BTW, Postcode, Bericht
  const isSimpleQuote = !!notes['Type'] && !notes['Stenen'];
  const simpleRows: { label: string; value: string }[] = [];
  if (isSimpleQuote) {
    if (notes['Type']) simpleRows.push({ label: 'Type klant', value: notes['Type'] });
    if (notes['Bedrijf']) simpleRows.push({ label: 'Bedrijf', value: notes['Bedrijf'] });
    if (notes['BTW']) simpleRows.push({ label: 'BTW-nummer', value: notes['BTW'] });
    if (notes['Postcode']) simpleRows.push({ label: 'Postcode', value: notes['Postcode'] });
    if (notes['Product']) simpleRows.push({ label: 'Product', value: notes['Product'] });
    if (notes['Oppervlakte']) {
      simpleRows.push({ label: 'Oppervlakte', value: notes['Oppervlakte'].includes('m²') ? notes['Oppervlakte'] : notes['Oppervlakte'] + ' m²' });
    }
    if (notes['Bericht']) simpleRows.push({ label: 'Bericht', value: notes['Bericht'] });
  }
  const detailsToShow = isSimpleQuote ? simpleRows : detailRows;

  // ─── V6 meersteens-offertes (fix Franky 19/8) ────────────────────────────
  // De configurator schrijft 'STEEN 1 — Product: … | Voegbreedte: … || STEEN 2
  // — …'. Het oude pad kende dat formaat niet, waardoor de PDF bijna leeg
  // bleef. Hier parsen we elke steen als eigen blok met al zijn instellingen
  // en prijsregels.
  interface SteenBlok { titel: string; velden: Record<string, string>; product?: typeof PRODUCTS[number] }
  const steenBlokken: SteenBlok[] = [];
  for (const deel of (q.notes_nl ?? '').split(' || ')) {
    const m = deel.match(/^STEEN\s+(\d+)\s+—\s+([\s\S]+)$/);
    if (!m) continue;
    const velden: Record<string, string> = {};
    for (const stuk of m[2].split(' | ')) {
      const kv = stuk.match(/^([^:]+):\s*(.+)$/);
      if (kv) velden[kv[1].trim()] = kv[2].trim();
    }
    steenBlokken.push({
      titel: `${t('pdf.cfg.stone') || 'Gevelsteen'} ${m[1]}`,
      velden,
      product: velden['Product'] ? PRODUCTS.find(pp => pp.name === velden['Product']) : undefined,
    });
  }
  const isMeersteens = steenBlokken.length > 0;

  // ─── Inline styles ──────────────────────────────────────────────────────
  // Geen DOCTYPE/html/head wrapper — chunks worden in een div geïnjecteerd
  // die door html2pdf naar PDF wordt gerenderd.
  wd(`<style>
  @media print { @page { margin: 8mm 10mm; size: A4 portrait; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; color: #1a1a1a; max-width: 800px; margin: 0 auto; padding: 8px 12px; font-size: 10px; line-height: 1.35; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #c47a3a; padding-bottom: 6px; margin-bottom: 8px; }
  .logo { font-size: 18px; font-weight: 800; color: #c47a3a; letter-spacing: -0.5px; }
  .logo-sub { font-size: 8.5px; color: #8a7e74; margin-top: 1px; font-weight: 400; }
  .quote-ref { text-align: right; font-size: 9.5px; color: #6b6b6b; }
  .quote-ref strong { display: block; font-size: 15px; color: #1a1a1a; margin-bottom: 1px; }
  .client-info { background: #faf7f3; border-radius: 5px; padding: 6px 10px; margin-bottom: 8px; display: flex; gap: 18px; }
  .client-info div { flex: 1; }
  .client-info .label { font-size: 7.5px; text-transform: uppercase; letter-spacing: 0.8px; color: #8a7e74; font-weight: 600; margin-bottom: 2px; }
  .client-info .value { font-size: 10.5px; font-weight: 600; }
  .section-title { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #c47a3a; margin: 8px 0 4px; }
  .product-block { display: flex; gap: 12px; margin-bottom: 6px; align-items: flex-start; }
  .product-img { width: 72px; height: 54px; border-radius: 5px; object-fit: cover; border: 1px solid #e5e5e5; flex-shrink: 0; }
  .product-meta { flex: 1; }
  .product-name { font-size: 13px; font-weight: 700; margin-bottom: 1px; }
  .product-code { font-size: 9.5px; color: #8a7e74; }
  table { width: 100%; border-collapse: collapse; margin: 2px 0 6px; }
  table.details td { padding: 2.5px 8px; border-bottom: 1px solid #f0ece8; font-size: 9.5px; }
  table.details td:first-child { color: #6b6b6b; width: 40%; font-weight: 500; }
  table.details td:last-child { font-weight: 500; }
  table.details tr:last-child td { border-bottom: none; }
  table.pricing th { text-align: left; padding: 3px 8px; font-size: 8.5px; text-transform: uppercase; letter-spacing: 0.8px; color: #8a7e74; border-bottom: 2px solid #e5e5e5; font-weight: 600; }
  table.pricing td { padding: 3.5px 8px; border-bottom: 1px solid #f0ece8; font-size: 10px; }
  table.pricing .subtotal td { font-weight: 500; }
  table.pricing .vat td { color: #6b6b6b; font-size: 9.5px; }
  table.pricing .total td { border-top: 2px solid #c47a3a; border-bottom: none; font-size: 12px; font-weight: 700; color: #c47a3a; padding-top: 5px; }
  table.pricing td:last-child, table.pricing th:last-child { text-align: right; }
  .conditions { margin-top: 6px; padding-top: 4px; border-top: 1px solid #e5e5e5; }
  .conditions h4 { font-size: 8.5px; text-transform: uppercase; letter-spacing: 1px; color: #8a7e74; font-weight: 600; margin-bottom: 3px; }
  .conditions ul { list-style: none; padding: 0; }
  .conditions li { font-size: 8.5px; color: #888; padding: 0.5px 0; padding-left: 10px; position: relative; }
  .conditions li::before { content: '\\2022'; position: absolute; left: 0; color: #c47a3a; }
  .footer { margin-top: 5px; text-align: center; font-size: 8.5px; color: #aaa; padding-top: 4px; border-top: 1px solid #f0ece8; }
</style>`);

  // ─── Header (logo + quote ref) ──────────────────────────────────────────
  wd(`<div class="header">`);
  if (isResellerRouted) {
    wd(`
  <div>
    <div style="font-size:9px;color:#8a7e74;letter-spacing:1px;text-transform:uppercase;margin-bottom:4px">${escapeHtml(t('pdf.poweredBy'))}</div>
    ${company.logo_url ? `<img src="${escapeHtml(company.logo_url)}" alt="${escapeHtml(company.name)}" style="max-height:36px;max-width:180px;margin-bottom:6px;display:block" onerror="this.style.display='none'" />` : ''}
    <div class="logo">${escapeHtml(company.name)}</div>
    <div class="logo-sub" style="margin-top:4px;color:#6b6b6b">
      ${escapeHtml([company.address, `${company.postalCode} ${company.city}`, company.country].filter(Boolean).join(' · '))}<br/>
      ${company.btw ? 'BTW ' + escapeHtml(company.btw) : ''} ${company.email ? '&middot; ' + escapeHtml(company.email) : ''} ${company.phone ? '&middot; ' + escapeHtml(company.phone) : ''}
    </div>
  </div>`);
  } else {
    wd(`
  <div>
    <div class="logo">${escapeHtml(company.name)}</div>
    <div class="logo-sub">Premium baksteenstrips &amp; gevelbekleding</div>
    <div class="logo-sub" style="margin-top:4px;color:#6b6b6b">
      ${escapeHtml(company.address)} &middot; ${escapeHtml(company.postalCode)} ${escapeHtml(company.city)}, ${escapeHtml(company.country)}<br/>
      ${company.btw ? 'BTW ' + escapeHtml(company.btw) + ' &middot; ' : ''}${escapeHtml(company.email)}${company.phone ? ' &middot; ' + escapeHtml(company.phone) : ''}
    </div>
  </div>`);
  }
  wd(`
  <div class="quote-ref">
    <span style="display:inline-block;background:#c47a3a;color:#fff;font-weight:800;letter-spacing:2px;font-size:13px;padding:4px 14px;border-radius:4px;margin-bottom:4px">${escapeHtml(t('pdf.quote')).toUpperCase()}</span><br/>
    ${escapeHtml(t('pdf.ref'))}: ${q.id.substring(0, 8).toUpperCase()}<br/>
    ${escapeHtml(t('pdf.date'))}: ${quoteDate}${expiryDate ? `<br/>${escapeHtml(t('pdf.validUntil'))}: ${expiryDate}` : ''}
  </div>
</div>`);

  // ─── Client info block ──────────────────────────────────────────────────
  // Defensieve fallbacks: ook bij oudere/halve quote-records moet er iets
  // herkenbaars onder KLANT staan zodat de PDF nooit "leeg" oogt.
  const clientName = (q.customer_name && q.customer_name.trim())
    || (q.customer_email ? q.customer_email.split('@')[0] : '')
    || '—';
  // B2B trigger ook op customer_btw_number — als BTW is ingevuld dan
  // is_b2b had 1 moeten zijn ook al staat-ie op 0 in de DB.
  const isB2BFlag = !!(q.is_b2b || (q.customer_btw_number && q.customer_btw_number.trim().length > 0));
  wd(`
<div class="client-info">
  <div>
    <div class="label">${escapeHtml(t('pdf.client'))}</div>
    <div class="value">${escapeHtml(clientName)}</div>
    ${q.customer_company_name ? `<div style="font-size:12px;color:#1a1a1a;font-weight:600;margin-top:1px">${escapeHtml(q.customer_company_name)}</div>` : ''}
    ${q.customer_email ? `<div style="font-size:12px;color:#6b6b6b;margin-top:2px">${escapeHtml(q.customer_email)}</div>` : ''}
    ${q.customer_phone ? `<div style="font-size:12px;color:#6b6b6b">${escapeHtml(q.customer_phone)}</div>` : ''}
    ${q.customer_address ? `<div style="font-size:12px;color:#6b6b6b;margin-top:2px">${escapeHtml(q.customer_address)}</div>` : ''}
  </div>
  <div>
    <div class="label">${escapeHtml(t('pdf.status'))}</div>
    <div class="value">${escapeHtml(t(`admin.status.${q.status}`) || q.status)}</div>
    ${isB2BFlag ? `<div style="font-size:12px;color:#6b6b6b;margin-top:2px">B2B${q.customer_btw_number ? ` — ${escapeHtml(q.customer_btw_number)}` : ''}</div>` : '<div style="font-size:12px;color:#6b6b6b;margin-top:2px">Particulier (B2C)</div>'}
    ${q.wants_installer ? `<div style="font-size:11px;color:#a8643a;font-weight:600;margin-top:4px">🔧 ${escapeHtml(t('pdf.installerWantedShort') || 'Installateur via ons netwerk')}</div>` : (q.installer_choice === 'self' ? `<div style="font-size:11px;color:#6b6b6b;margin-top:4px">${escapeHtml(t('pdf.installerSelfShort') || 'Klant plaatst zelf')}</div>` : '')}
  </div>
</div>`);

  // ─── Werkadres-blok (alleen als apart werkadres is opgegeven) ──────────
  // Filip / reseller leeft van duidelijkheid: één foutief adres en de
  // levering staat 2 dagen stil. Opvallend kader, gele rand, "Plaatsing op:"
  // — niet te missen.
  const installAddr  = (q.installation_address || '').trim();
  const installPC    = (q.installation_postal_code || '').trim();
  const installCity  = (q.installation_city || '').trim();
  const hasInstallAddr = installAddr || installPC || installCity;
  if (hasInstallAddr) {
    const installLine = [installAddr, [installPC, installCity].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', ');
    wd(`
<div style="border:2px solid #c47a3a;background:#fff8ef;border-radius:6px;padding:10px 14px;margin-bottom:14px;display:flex;align-items:center;gap:12px">
  <div style="font-size:18px;line-height:1">📍</div>
  <div style="flex:1">
    <div style="font-size:8px;text-transform:uppercase;letter-spacing:1.2px;color:#a8643a;font-weight:700">
      ${escapeHtml(t('pdf.installAddressLabel') || 'Werkadres / Plaatsing')}
    </div>
    <div style="font-size:13px;font-weight:700;color:#1a1a1a;margin-top:2px">${escapeHtml(installLine)}</div>
    <div style="font-size:9px;color:#8a7e74;margin-top:1px;font-style:italic">
      ${escapeHtml(t('pdf.installAddressNote') || 'Levering en plaatsing gebeurt op dit adres — niet op het factuuradres.')}
    </div>
  </div>
</div>`);
  } else {
    // v3.2.71 — placeholder-blok wanneer leverings/plaatsings-adres nog niet
    // gekend is. Filip: 'die info volgt dan later wel als de deal rond is, of
    // tenzij ze zelf installeren'. Toont expliciet aan klant dat dit veld
    // bestaat en bij bevestiging ingevuld wordt — geen verborgen verrassing.
    wd(`
<div style="border:1px dashed #c47a3a;background:#fafafa;border-radius:6px;padding:10px 14px;margin-bottom:14px;display:flex;align-items:flex-start;gap:12px">
  <div style="font-size:14px;line-height:1.2">📦</div>
  <div style="flex:1">
    <div style="font-size:8px;text-transform:uppercase;letter-spacing:1.2px;color:#a8643a;font-weight:700">
      ${escapeHtml(t('pdf.deliveryHeading'))}
    </div>
    <div style="font-size:11px;color:#4a4a4a;margin-top:2px">
      ${escapeHtml(t('pdf.deliveryNote1'))}
    </div>
    <div style="font-size:9px;color:#8a7e74;margin-top:1px;font-style:italic">
      ${escapeHtml(t('pdf.deliveryNote2'))}
    </div>
  </div>
</div>`);
  }

  // ─── Product block with image ───────────────────────────────────────────
  if (product) {
    wd(`<div class="section-title">${escapeHtml(t('pdf.product'))}</div>`);
    wd(`<div class="product-block">`);
    if (imageUrl) {
      wd(`<img class="product-img" src="${imageUrl}" alt="${escapeHtml(product.name)}" onerror="this.style.display='none'" />`);
    }
    wd(`<div class="product-meta">
  <div class="product-name">${escapeHtml(product.name)}</div>
  <div class="product-code">${escapeHtml(product.supplierCode)} &bull; ${escapeHtml(product.category)}</div>
  <div style="font-size:12px;color:#6b6b6b;margin-top:6px">${escapeHtml(product.description)}</div>
</div></div>`);
  }

  // ─── V6: blok per steen (meersteens-offerte) ────────────────────────────
  if (isMeersteens) {
    for (const blok of steenBlokken) {
      const img = blok.product?.images?.[0] ? `${window.location.origin}${blok.product.images[0]}` : '';
      const veldOrde: Array<[string, string]> = [
        ['Oppervlakte', t('pdf.cfg.area') || 'Oppervlakte'],
        ['Voegbreedte', t('pdf.cfg.jointWidth') || 'Voegbreedte'],
        ['Voegkleur', t('pdf.cfg.groutColor') || 'Voegkleur'],
        ['Snijverlies', t('pdf.cfg.cuttingLoss') || 'Snijverlies'],
        ['Hoeken', t('pdf.cfg.corners') || 'Hoeken'],
        ['Stenen', t('pdf.cfg.stones') || 'Stenen'],
        ['Levertermijn', t('pdf.cfg.delivery') || 'Levertermijn'],
      ];
      const prijsOrde: Array<[string, string]> = [
        ['Baksteenstrips', t('pdf.cfg.bricks') || 'Baksteenstrips'],
        ['Hoekstenen', t('pdf.cfg.cornerStones') || 'Hoekstenen'],
        ['Lijm', t('pdf.cfg.glue') || 'Lijm'],
        ['Voegsel', t('pdf.cfg.grout') || 'Voegsel'],
        ['Subtotaal excl. BTW', t('pdf.subtotalExcl') || 'Subtotaal excl. BTW'],
      ];
      wd(`<div class="section-title">${escapeHtml(blok.titel)}${blok.velden['Product'] ? ' — ' + escapeHtml(blok.velden['Product']) : ''}</div>`);
      wd(`<div class="product-block" style="margin-bottom:2px">`);
      if (img) wd(`<img class="product-img" src="${img}" alt="" onerror="this.style.display='none'" style="width:74px;height:52px;object-fit:cover;border-radius:4px" />`);
      wd(`<table class="details" style="margin-top:0;flex:1">`);
      for (const [key, label] of veldOrde) {
        if (blok.velden[key]) wd(`<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(localizeValue(blok.velden[key]))}</td></tr>`);
      }
      if (blok.velden['Kleur']) wd(`<tr><td>${escapeHtml(t('pdf.cfg.color') || 'Kleur / textuur')}</td><td>${escapeHtml(localizeValue(blok.velden['Kleur']))}</td></tr>`);
      wd(`</table></div>`);
      wd(`<table class="pricing" style="margin-bottom:8px"><tbody>`);
      for (const [key, label] of prijsOrde) {
        const v = blok.velden[key];
        if (!v) continue;
        const bedrag = v.match(/€\s?[\d.,]+/)?.[0] ?? v;
        wd(`<tr><td>${escapeHtml(label)}${key === 'Lijm' || key === 'Voegsel' ? ` <span style=\"color:#8a7e74;font-size:8.5px\">(${escapeHtml(v.replace(/—?\s*€[\d.,]+/, '').trim())})</span>` : ''}</td><td>${escapeHtml(bedrag)}</td></tr>`);
      }
      wd(`</tbody></table>`);
    }
  }

  // ─── Configuration details ──────────────────────────────────────────────
  if (!isMeersteens && detailsToShow.length > 0) {
    wd(`<div class="section-title">${escapeHtml(isSimpleQuote ? t('pdf.requestDetails') : t('pdf.configuration'))}</div>`);
    wd(`<table class="details">`);
    for (const row of detailsToShow) {
      wd(`<tr><td>${escapeHtml(row.label)}</td><td>${escapeHtml(row.value)}</td></tr>`);
    }
    wd(`</table>`);
  }

  // ─── Price overview ─────────────────────────────────────────────────────
  // Filip's wens: maak duidelijk dat eenheidsprijzen per vierkante meter zijn —
  // anders denken klanten dat de getoonde €48 het totaal is voor de hele gevel,
  // wat tot pijnlijke misverstanden leidt bij de factuur.
  wd(`<div class="section-title" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
    <span>${escapeHtml(t('pdf.priceOverview'))}</span>
    <span style="font-size:8px;background:#fff8ef;color:#a8643a;border:1px solid #c47a3a;padding:1px 5px;border-radius:3px;letter-spacing:0.5px;font-weight:700">${escapeHtml(t('pdf.unitPriceM2') || 'EENHEIDSPRIJZEN PER M²')}</span>
  </div>`);
  wd(`<table class="pricing">
<thead><tr><th>${escapeHtml(t('pdf.description'))}</th><th>${escapeHtml(t('pdf.amount'))}</th></tr></thead><tbody>`);
  if (priceRows.length > 0 && !isSimpleQuote && !isMeersteens) {
    for (const row of priceRows) {
      // Append /m² aan ruwe prijswaarden die nog geen eenheid hebben.
      // Het regex pakt €X,XX of €X.XX zonder bestaande /m². Anders behouden
      // we de waarde zoals ze al was geformatteerd door de calculator.
      const v = row.value;
      const hasUnit = /\/m²|m2|per\s*m/i.test(v);
      const decoratedValue = hasUnit ? v : (/^€?[\d.,]+$/.test(v.trim()) ? `${v}/m²` : v);
      wd(`<tr><td>${escapeHtml(row.label)}</td><td>${escapeHtml(decoratedValue)}</td></tr>`);
    }
    // Visuele scheiding tussen eenheidsprijzen (per m²) en de offertetotalen
    // verderop — voorkomt dat klanten denken dat het subtotaal ook per m² is.
    wd(`<tr><td colspan="2" style="padding:4px 8px 2px 8px;font-size:8px;color:#8a7e74;text-transform:uppercase;letter-spacing:0.8px;font-style:italic;border-bottom:1px dashed #d4d4d4">${escapeHtml(t('pdf.totalsForQuote') || 'Totalen voor deze offerte')}</td></tr>`);
  }
  // BTW: B2B → split, B2C → single total + "BTW inbegrepen" footnote
  const isB2B = !!(q.is_b2b || (q.customer_btw_number && q.customer_btw_number.trim().length > 0));
  if (isB2B) {
    wd(`<tr class="subtotal"><td>${escapeHtml(t('pdf.subtotalExcl'))}</td><td>${formatEuro(q.total_excl_vat)}</td></tr>`);
    wd(`<tr class="vat"><td>${escapeHtml(t('pdf.vat21'))}</td><td>${formatEuro(vatAmount)}</td></tr>`);
    wd(`<tr class="total"><td>${escapeHtml(t('pdf.totalIncl'))}</td><td>${formatEuro(q.total_incl_vat)}</td></tr>`);
  } else {
    wd(`<tr class="total"><td>${escapeHtml(t('pdf.totalPrice'))}</td><td>${formatEuro(q.total_incl_vat)}</td></tr>`);
    wd(`<tr class="vat"><td colspan="2" style="text-align:right;font-style:italic">${escapeHtml(t('pdf.vatIncluded'))}</td></tr>`);
  }
  wd(`</tbody></table>`);

  // ─── Standard conditions ────────────────────────────────────────────────
  wd(`
<div class="conditions">
  <h4>${escapeHtml(t('pdf.conditions'))}</h4>
  <ul>
    <li>${escapeHtml(t('pdf.cond1'))}</li>
    <li>${escapeHtml(t('pdf.cond2'))}</li>
    <li>${escapeHtml(t('pdf.cond3'))}</li>
    <li>${escapeHtml(t('pdf.cond4'))}</li>
  </ul>
</div>`);

  // ─── Footer ─────────────────────────────────────────────────────────────
  wd(`
<div class="footer">
  ${escapeHtml(company.name)}${company.address ? ' &mdash; ' + escapeHtml(company.address) : ''}${company.city ? ', ' + escapeHtml(company.postalCode) + ' ' + escapeHtml(company.city) : ''}${company.btw ? ' &mdash; BTW ' + escapeHtml(company.btw) : ''}<br/>
  ${escapeHtml(company.email)}${company.phone ? ' &nbsp;|&nbsp; ' + escapeHtml(company.phone) : ''}
  ${isResellerRouted ? `<br/><span style="color:#aaa;font-size:8px">${escapeHtml(t('pdf.installerNote'))}</span>` : ''}
</div>`);

  // ─── Render via browser print-window (v3.2.124 — terug naar gisteren) ──
  // Gisteren werkte de PDF perfect via window.print() in een nieuw tabblad.
  // De pop-up blocker triggerde toen de window.open() ná async work werd
  // aangeroepen. Fix: caller opent het venster SYNCHROON in de click-handler
  // en geeft het door via opts.targetWindow. Wij schrijven de HTML er
  // achteraf in en triggeren auto-print zodat de gebruiker direct in de
  // print-dialoog "Bewaar als PDF" kan kiezen.
  const now = new Date();
  const yyyymmdd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const hhmm = `${String(now.getHours()).padStart(2, '0')}h${String(now.getMinutes()).padStart(2, '0')}`;
  const safeName = (q.customer_name || 'klant').replace(/[^\w-]+/g, '_');
  const filename = `Offerte_${q.id.substring(0, 8).toUpperCase()}_${safeName}_${yyyymmdd}_${hhmm}.pdf`;
  // V5.34 (wens Franky): standaard eerst een voorbeeld op ware grootte ter
  // controle — printen/bewaren gebeurt pas via de knop in het voorbeeld.
  writeChunksToPrintWindow(chunks, filename, opts.mode ?? 'preview', opts.targetWindow ?? null);
}

// ─── Print-window renderer (v3.2.124) ───────────────────────────────────────
// Schrijft de chunks-HTML in een (vooraf-geopend) browser-venster en
// triggert auto-print zodat de gebruiker direct kan kiezen voor
// "Bewaar als PDF". Werkt 100% in elke browser want de native print-dialoog
// rendert de HTML met echte vector-tekst — geen html2canvas-issues.
function writeChunksToPrintWindow(
  chunks: string[],
  filename: string,
  mode: 'download' | 'preview',
  prepared: Window | null,
): void {
  const title = filename.replace(/\.pdf$/, '');
  const autoPrint = mode === 'download';
  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${title}</title>
<script>
window.addEventListener('load', function() {
  ${autoPrint ? `setTimeout(function(){ try { window.focus(); window.print(); } catch(e) { console.error('print failed', e); } }, 350);` : ''}
});
</script>
</head>
<body>
${chunks.join('')}
<div class="no-print pdfknoppen">
  <button class="pk-bewaar" onclick="window.print()">⬇️ Bewaar als PDF</button>
  <button class="pk-sluit" onclick="window.close(); setTimeout(function(){ if (history.length > 1) history.back(); }, 250);">✕ Sluiten</button>
</div>
<style>
  .pdfknoppen{position:fixed;top:8px;right:8px;z-index:9999;display:flex;gap:8px}
  .pk-bewaar{padding:8px 16px;background:#c47a3a;color:#fff;border:0;border-radius:6px;font-weight:700;font-size:13px;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,0.15)}
  .pk-sluit{padding:8px 16px;background:#fff;color:#4a4a4a;border:1px solid #ddd;border-radius:6px;font-weight:600;font-size:13px;cursor:pointer}
  /* Gsm (Franky 19/8): grote vaste balk onderaan — duidelijk bewaren én
     duidelijk terug eruit */
  @media (max-width: 640px) {
    body { padding-bottom: 92px !important; }
    .pdfknoppen{top:auto;bottom:0;left:0;right:0;background:#1a1a1a;
      padding:12px 12px calc(12px + env(safe-area-inset-bottom));gap:10px}
    .pk-bewaar{flex:1;padding:15px;font-size:16px;border-radius:10px}
    .pk-sluit{padding:15px 20px;font-size:16px;border-radius:10px;background:#fff}
  }
  @media print { .no-print { display:none !important } }
</style>
</body>
</html>`;

  // Gebruik het vooraf-geopende venster als beschikbaar; anders fallback
  // (werkt alleen als pop-up blocker mild is, bv. voor admin op desktop).
  const w = prepared || window.open('', '_blank');
  if (!w) {
    alert('Pop-up blocker actief. Sta pop-ups toe voor deze site en probeer opnieuw.');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

// ─── (LEGACY, niet meer gebruikt) Direct-download PDF renderer ─────────────
// Bewust behouden voor referentie. NIET aanroepen — gebruikt html2canvas die
// in praktijk leeg/halve render gaf in de iframe-context.
async function renderChunksToDirectPdf(
  chunks: string[],
  filename: string,
  mode: 'download' | 'preview',
): Promise<void> {
  const [{ jsPDF }, h2cMod] = await Promise.all([
    import('jspdf'),
    import('html2canvas'),
  ]);
  const html2canvas = h2cMod.default;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${chunks.join('')}</body></html>`;
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:800px;height:1200px;border:0;';
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    if (!doc) throw new Error('Kon iframe-document niet aanmaken');
    doc.open();
    doc.write(html);
    doc.close();

    // Wacht tot images (productfoto, logo) geladen zijn voor we renderen.
    await new Promise<void>((resolve) => {
      const imgs = Array.from(doc.querySelectorAll('img'));
      if (imgs.length === 0) { resolve(); return; }
      let remaining = imgs.length;
      const done = () => { if (--remaining <= 0) resolve(); };
      imgs.forEach((img) => {
        if (img.complete) { done(); return; }
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
      // Hard limit van 3s zodat een trage CDN ons niet ophoudt.
      setTimeout(resolve, 3000);
    });
    // Extra rust-tijd voor fonts en layout-reflow.
    await new Promise((r) => setTimeout(r, 150));

    const body = doc.body;
    const canvas = await html2canvas(body, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: body.scrollWidth,
      height: body.scrollHeight,
      windowWidth: 800,
    });

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    const imgW = pageW;
    const imgH = (canvas.height * imgW) / canvas.width;

    if (imgH <= pageH) {
      pdf.addImage(imgData, 'JPEG', 0, 0, imgW, imgH);
    } else {
      // Multi-page: dezelfde image herhaald aangeboden met negatieve y-offset
      // per pagina — jsPDF clipt automatisch op A4 grenzen.
      let position = 0;
      let heightLeft = imgH;
      pdf.addImage(imgData, 'JPEG', 0, position, imgW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgW, imgH);
        heightLeft -= pageH;
      }
    }

    if (mode === 'preview') {
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) {
        // Pop-up geblokkeerd — val terug op directe download zodat de
        // gebruiker het bestand alsnog krijgt.
        pdf.save(filename);
      } else {
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } else {
      pdf.save(filename);
    }
  } finally {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }
}

// ─── jsPDF-based direct PDF builder (v3.2.113) ─────────────────────────────
interface BuildPdfArgs {
  filename: string;
  company: PdfCompany;
  quote: PdfQuote;
  isResellerRouted: boolean;
  quoteDate: string;
  expiryDate: string;
  detailRows: { label: string; value: string }[];
  priceRows: { label: string; value: string }[];
  vatAmount: number;
  t: (key: string) => string;
  mode: 'download' | 'preview';
}

async function buildPdfWithJsPdf(args: BuildPdfArgs): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  const { company, quote: q, isResellerRouted, quoteDate, expiryDate, detailRows, priceRows, vatAmount, t } = args;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  const accent: [number, number, number] = [196, 122, 58];   // #c47a3a
  const text: [number, number, number]   = [26, 26, 26];     // #1a1a1a
  const muted: [number, number, number]  = [110, 110, 110];

  // ── Header: company info (links) + quote-ref (rechts) ──────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...accent);
  doc.text(company.name, margin, 18);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...muted);
  let y = 23;
  const addrLine = [company.address, [company.postalCode, company.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
  if (addrLine) { doc.text(addrLine, margin, y); y += 4; }
  const contactLine = [company.btw && `BTW ${company.btw}`, company.email, company.phone].filter(Boolean).join(' · ');
  if (contactLine) { doc.text(contactLine, margin, y); y += 4; }

  // Quote-ref block rechts
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...text);
  const ref = `Q-${q.id.substring(0, 8).toUpperCase()}`;
  doc.text(ref, pageW - margin, 18, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...muted);
  doc.text(`Datum: ${quoteDate}`, pageW - margin, 23, { align: 'right' });
  if (expiryDate) doc.text(`Geldig tot: ${expiryDate}`, pageW - margin, 27, { align: 'right' });

  // Horizontale accent-lijn
  y = 32;
  doc.setDrawColor(...accent);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 6;

  // ── Klant-info blok ─────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...muted);
  doc.text('KLANT', margin, y);
  doc.text('STATUS', pageW - margin - 50, y);
  y += 4;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...text);
  doc.text(q.customer_name || '—', margin, y);
  const statusLabels: Record<string, string> = { draft: 'Concept', sent: 'Verstuurd', accepted: 'Goedgekeurd', rejected: 'Afgewezen', expired: 'Verlopen' };
  doc.text(statusLabels[q.status] || q.status, pageW - margin - 50, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...muted);
  if (q.customer_email) { doc.text(q.customer_email, margin, y); y += 4; }
  if (q.customer_phone) { doc.text(q.customer_phone, margin, y); y += 4; }
  if (q.customer_btw_number) { doc.text(`BTW ${q.customer_btw_number}`, margin, y); y += 4; }
  if (q.customer_address) { doc.text(q.customer_address, margin, y); y += 4; }
  if (q.is_b2b) doc.text('B2B-klant', pageW - margin - 50, y - 4);

  // ── Installatie-adres (indien afwijkend) ────────────────────────────────
  if (q.installation_address && (q.installation_address !== q.customer_address)) {
    y += 3;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...accent);
    doc.text('PLAATSING OP', margin, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...text);
    const instLine = [q.installation_address, [q.installation_postal_code, q.installation_city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    doc.text(instLine, margin, y);
    y += 5;
  }

  y += 4;

  // ── Specificaties tabel ────────────────────────────────────────────────
  if (detailRows.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...accent);
    doc.text('SPECIFICATIES', margin, y);
    y += 2;
    autoTable(doc, {
      startY: y,
      head: [],
      body: detailRows.map(r => [r.label, r.value]),
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: { top: 1.5, bottom: 1.5, left: 0, right: 4 }, textColor: text },
      columnStyles: { 0: { textColor: muted, cellWidth: 50 }, 1: { fontStyle: 'bold' } },
      margin: { left: margin, right: margin },
    });
    y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y;
    y += 6;
  }

  // ── Prijs breakdown ─────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...accent);
  doc.text('PRIJSOVERZICHT', margin, y);
  y += 2;

  const priceBody: (string | number)[][] = [];
  for (const r of priceRows) priceBody.push([r.label, r.value]);
  priceBody.push(['Subtotaal (excl. BTW)', formatEuro(q.total_excl_vat)]);
  priceBody.push([`BTW`, formatEuro(vatAmount)]);
  autoTable(doc, {
    startY: y,
    head: [],
    body: priceBody,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: { top: 1.8, bottom: 1.8, left: 0, right: 4 }, textColor: text },
    columnStyles: { 0: { textColor: muted }, 1: { halign: 'right', fontStyle: 'bold' } },
    margin: { left: margin, right: margin },
  });
  y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y;

  // Totaal incl. BTW — dik accent
  y += 3;
  doc.setDrawColor(...accent);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...accent);
  doc.text('TOTAAL (incl. BTW)', margin, y);
  doc.text(formatEuro(q.total_incl_vat), pageW - margin, y, { align: 'right' });
  y += 10;

  // ── Voorwaarden ─────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...accent);
  doc.text('VOORWAARDEN', margin, y);
  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...muted);
  const conds = [
    'Deze offerte is een richtprijs, geen contractueel bindend document.',
    'Geldig tot ' + (expiryDate || '30 dagen na aanmaak') + '.',
    'Prijzen inclusief 21% BTW (tenzij anders vermeld voor renovatie aan 6%).',
    'Levertijd: 5–10 werkdagen vanaf bevestiging.',
  ];
  for (const c of conds) {
    doc.text('•  ' + c, margin, y, { maxWidth: pageW - margin * 2 });
    y += 4;
  }

  // ── Footer ──────────────────────────────────────────────────────────────
  const pageH = doc.internal.pageSize.getHeight();
  doc.setFontSize(7);
  doc.setTextColor(...muted);
  const footerLine = [
    company.name,
    company.address,
    [company.postalCode, company.city].filter(Boolean).join(' '),
    company.btw && `BTW ${company.btw}`,
    company.email,
    company.phone,
  ].filter(Boolean).join(' · ');
  doc.text(footerLine, pageW / 2, pageH - 10, { align: 'center', maxWidth: pageW - margin * 2 });
  if (isResellerRouted) {
    doc.text(t('pdf.installerNote'), pageW / 2, pageH - 6, { align: 'center' });
  }

  // ── Output: download of preview in nieuw tabblad ───────────────────────
  if (args.mode === 'preview') {
    // Blob URL openen in een nieuw tabblad — browser toont native PDF
    // viewer waar Filip kan scrollen, zoomen, en daarna downloaden.
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (!w) {
      // Pop-up blocker actief — val terug op download zodat Filip
      // het bestand alsnog krijgt.
      console.warn('[PDF] preview pop-up blocked, falling back to download');
      doc.save(args.filename);
    } else {
      // Revoke de blob URL na 60s — genoeg tijd om de PDF te laden.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  } else {
    doc.save(args.filename);
  }
}

// ─── Helper — render + ECHTE PDF download ─────────────────────────────────
// v3.2.99 — Filip vroeg: 'de download moet in pdf niet in html zijn ...
// of haal de opslaan als pdf weg ... maak er een pdf van die je direct
// kan downloaden'. Nu: één 'Download PDF'-knop die html2pdf.js gebruikt
// om het iframe-document om te zetten naar een echte .pdf (via canvas +
// jsPDF). Bedanken doen we de iframe nog steeds voor preview; de
// 'Opslaan als PDF'-knop (die enkel het print-dialoog opende) is weg.
async function renderHtmlChunksToPdf(chunks: string[], filename: string): Promise<void> {
  const html = `<!DOCTYPE html><html><head><title>${filename.replace(/\.pdf$/, '')}</title><meta charset="utf-8"></head><body>${chunks.join('')}</body></html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const blobUrl = URL.createObjectURL(blob);

  const backdrop = document.createElement('div');
  backdrop.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.65);z-index:99999;display:flex;align-items:center;justify-content:center;padding:16px;backdrop-filter:blur(4px);';

  const modal = document.createElement('div');
  modal.style.cssText = 'background:#fff;border-radius:12px;width:100%;max-width:900px;height:92vh;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,0.4);overflow:hidden;';

  const header = document.createElement('div');
  header.style.cssText = 'padding:14px 20px;border-bottom:1px solid #eee;display:flex;align-items:center;justify-content:space-between;background:linear-gradient(to right, #faf7f3, #fff8ef);gap:12px;';
  header.innerHTML = `
    <div style="min-width:0;flex:1">
      <div style="font-size:11px;font-weight:700;letter-spacing:0.8px;color:#8a7e74;text-transform:uppercase">PDF Voorbeeld</div>
      <div style="font-size:13px;font-weight:600;color:#1a1a1a;font-family:monospace;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${filename}</div>
    </div>
    <div style="display:flex;gap:6px;flex-shrink:0">
      <button id="pdfm-download" style="padding:8px 14px;background:#c47a3a;color:#fff;border:0;border-radius:6px;font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap">
        ⬇️ Bewaar als PDF
      </button>
      <button id="pdfm-close" style="padding:8px 14px;background:#fff;color:#4a4a4a;border:1px solid #ddd;border-radius:6px;font-weight:500;font-size:13px;cursor:pointer">
        Sluiten
      </button>
    </div>
    <div style="grid-column:1/-1;font-size:11px;color:#8a7e74;margin-top:4px;flex-basis:100%">
      💡 Klik op <strong>Bewaar als PDF</strong> → kies in de print-dialoog <strong>Bestemming → Bewaar als PDF</strong> en klik onderaan op <strong>Bewaren</strong>.
    </div>
  `;

  const iframe = document.createElement('iframe');
  iframe.src = blobUrl;
  iframe.style.cssText = 'flex:1;width:100%;border:0;background:#f5f5f5;';
  iframe.title = filename;

  modal.appendChild(header);
  modal.appendChild(iframe);
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);

  const cleanup = () => {
    if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
    document.removeEventListener('keydown', onEsc);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  };
  const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') cleanup(); };
  document.addEventListener('keydown', onEsc);
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) cleanup(); });

  modal.querySelector<HTMLButtonElement>('#pdfm-close')?.addEventListener('click', cleanup);

  const downloadBtn = modal.querySelector<HTMLButtonElement>('#pdfm-download');
  downloadBtn?.addEventListener('click', () => {
    // v3.2.111 — html2pdf is volledig verlaten. In productie was 't te
    // onbetrouwbaar (lege PDFs door cross-document html2canvas, CORS-issues
    // met product-images, hangende calls). Native print-dialoog werkt
    // 100% in alle browsers en geeft betere kwaliteit (echte vector-tekst
    // i.p.v. canvas-bitmap). Filip ziet hint onder de knoppen die uitlegt
    // hoe 'Bewaar als PDF' werkt.
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (err) {
      console.error('[PDF] iframe.print() failed:', err);
      const w = window.open(blobUrl, '_blank');
      if (!w) {
        alert('Pop-up blocker actief. Schakel pop-ups in voor deze site en probeer opnieuw, of druk Cmd/Ctrl+P binnen de preview.');
      }
    }
  });
}

/** Owner-side default company block. Use when generating customer-facing PDFs
 *  where there is no reseller in between. */
// v5 blok 12: branding centraal — één plek (src/config/brand.ts) stuurt
// site, footer, contact én alle PDF's aan.
import { BRAND } from '../config/brand';
export const OWNER_COMPANY: PdfCompany = {
  name:       BRAND.name,
  address:    BRAND.street,
  postalCode: BRAND.postalCode,
  city:       BRAND.city,
  country:    BRAND.country,
  btw:        BRAND.btw,
  email:      BRAND.email,
  phone:      BRAND.phone,
  logo_url:   '',
};
